/**
 * MediaDetailsPage — directly-routable chat-media viewer (`/chat/media/:id`,
 * where `:id` is the chat message `documentId`).
 *
 * Distinct from `ContentDetailsPage` (user-generated content): this page renders
 * a single chat message's client-E2EE media. The chat message + the peer's
 * public JWK arrive ONLY as route args (`location.state`, set by
 * `Navigator.navigateToMediaDetails` — call-sites land in Task 15). A pasted /
 * shared URL has no args, so the page surfaces an error and NEVER fetches the
 * message by id (spec point 7 / consideration B — this is the deliberate
 * anti-leak behaviour).
 *
 * Page + feature-hook split (see `presentation.md`): the page owns `useBasePage`,
 * the `screenKey` mint, `useParams`, the `location.state` snapshot, and the auth
 * gate; `useMediaDetails` owns state + the decrypt side-effects and exposes the
 * `onPageInit` lifecycle bridge + `isFirstLoad`.
 *
 * IMAGE branch: the hook resolves the encrypted full-image ciphertext's
 * BUCKET-RELATIVE object path and the page renders it through
 * `EncryptedCachedImage` (resolve tokenized download URL via the domain UseCase
 * → fetch → decrypt → `blob:` + shared LRU — the same component the inline
 * `MediaBubbleFrame` thumbnail uses, Finding 5), passing the route-arg peer JWK.
 * VIDEO branch: when the hook resolves
 * `media.kind === MediaType.VIDEO` it carries the chat Media CDN `videoUrl` +
 * decrypted keys, which the page hands to the shared `InlineVideoPlayer` (themed
 * encrypted-HLS surface with a progress bar + native fullscreen — the same
 * playback surface used elsewhere in the app), passing the message's E2EE
 * thumbnail (via `EncryptedCachedImage`) for the pre-playback poster.
 *
 * App bar: the shared `ExpauseAppBar`
 * (`presentation/components/common/appBar/ExpauseAppBar.tsx`), which mirrors
 * Flutter's media-viewer bar (`content_list_widget.dart:1112-1147` — back
 * chevron `leading` → `_onBackPress`, `centerTitle: true`, a `.6`-alpha +
 * `BackdropFilter` background). Moving off the hand-rolled row makes four
 * intentional visual changes: the title is now **centered** (Flutter
 * `centerTitle: true`, `base_app_bar.dart:31` — the old left-aligned title was
 * the web-only outlier), it steps from `responsive.font.lg`/`semibold` to
 * `responsive.font.xl`/`bold`, the bar becomes sticky with a blurred
 * 50%-opacity backdrop, and `responsive.safeArea.top` is now honoured. No
 * `leadingQa` is passed, so the back control keeps `BackIconButton`'s default
 * `data-qa-id="back-button"`.
 *
 * Title text is a deliberate web addition — Flutter renders this viewer's bar
 * title-less (`content_list_widget.dart:1126`, `title: Container()`); the web
 * keeps `l10n.chat.mediaDetails.title` so a deep-linked viewer names its
 * surface. `DefaultMediaPage` matches Flutter and passes no `title`.
 *
 * The bar renders on the first-load branch too: Flutter builds the same
 * back-arrow bar over its empty/loading body
 * (`content_list_widget.dart:1029-1048`, `_isFetching` →
 * `ExpauseLoadingIndicator` at `:1071-1078`), and `showFirstLoad` also covers
 * the auth-resolution window, so gating the back affordance on it could strand
 * the user on a spinner (`workflow/lessons.md`).
 */

import { useCallback, useMemo, useRef, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';

import { MediaType } from '@/domain/enums/mediaType';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { useBasePage } from '@/presentation/hooks/useBasePage';
import { useKeepAlive } from '@/presentation/routing/RouteStack';
import { generateScreenKey } from '@/presentation/routing/screenKeyUtils';
import { Navigator } from '@/presentation/routing/Navigator';
import { ExpauseAppBar } from '@/presentation/components/common/appBar/ExpauseAppBar';
import { ExpauseLoadingIndicator } from '@/presentation/components/common/loadingIndicators/ExpauseLoadingIndicator';
import { EncryptedCachedImage } from '@/presentation/components/common/EncryptedCachedImage';
import { qaAttr } from '@/presentation/utils/qaAttr';

import type { RootState } from '@/presentation/store';

import {
  useMediaDetails,
  type MediaDetailsRouteArgs,
} from './hooks/useMediaDetails';
import { InlineVideoPlayer } from '@/presentation/components/video/InlineVideoPlayer';

export const MediaDetailsPage = () => {
  const { id: documentId } = useParams<{ id: string }>();
  const location = useLocation();
  const { theme } = useTheme();
  const { l10n } = useLocale();
  const responsive = useResponsiveDesign();

  const currentUser = useSelector((state: RootState) => state.auth.user);

  // Canonical screen-key wiring — captured ONCE on mount (same pattern as
  // ChatPage / ContentDetailsPage). The `documentId` in the URL keeps the
  // route-caching mechanism functioning (each opened media has a unique id).
  const { currentScreenKeyRef } = useKeepAlive();
  const screenKeyRef = useRef<string>(
    currentScreenKeyRef.current || generateScreenKey(location.pathname),
  );
  const screenKey = screenKeyRef.current;

  // Snapshot of `location.state` (the route args), taken ONCE at mount. `null`
  // on the pasted / shared-URL path — the hook's anti-leak gate fires and the
  // page renders the arg-missing error WITHOUT a by-id fetch.
  const routeArgs = useMemo<MediaDetailsRouteArgs | null>(() => {
    const state = location.state as MediaDetailsRouteArgs | undefined;
    if (state && state.message) {
      return state;
    }
    return null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const {
    media,
    status,
    isFirstLoad,
    errorMessage,
    onPageInit,
    onImageError,
  } = useMediaDetails({ documentId, routeArgs });

  // Page-only `useBasePage` — bridges the lifecycle into the hook's memoized
  // `onPageInit`. The hook owns every state mutation `onInit` triggers.
  useBasePage({
    screenKey,
    onInit: onPageInit,
    debug: false,
  });

  // First-load gate (see `presentation.md`): the page spinner is gated on the
  // hook's own `isFirstLoad` (covers the decrypt round-trip) plus the auth gate.
  const showFirstLoad = isFirstLoad || currentUser === null;

  // Post-init (mid-playback) fatal error from the player. The hook owns INIT
  // failures (→ page `status==='error'`); this covers a fatal HLS error AFTER
  // the player mounted, surfacing the same localized video-error message.
  const [videoPlaybackError, setVideoPlaybackError] = useState(false);

  // Leading (back) handler, declared ABOVE the first-load early return so both
  // render branches share one instance and the hook order is identical in every
  // state (same shape as `UserVideosPage` / `ContentDetailsPage`).
  //
  // Deep-link fallback is the Feed, NOT the originating chat thread: the peer is
  // only ever knowable from `routeArgs`, and `routeArgs` is non-null exactly when
  // the page was pushed in-app — which is exactly when there IS history to pop
  // and the fallback never runs. The branch that fires (pasted / new-tab URL) is
  // by construction the branch with no args, no peer id anywhere in the
  // `/chat/media/:id` URL, and therefore no thread to return to. `ChatPage`
  // itself falls back to the Feed for the same reason.
  const handleBack = useCallback(() => {
    Navigator.goBackOr(() => Navigator.navigateToFeed());
  }, []);

  const containerStyle = {
    minHeight: '100vh',
    backgroundColor: theme.background,
  } as const;

  if (showFirstLoad) {
    return (
      <div
        {...qaAttr({ id: 'media-details', status: 'loading' })}
        style={{ ...containerStyle, display: 'flex', flexDirection: 'column' }}
      >
        <ExpauseAppBar
          rootQa={{ id: 'media-details-app-bar' }}
          title={l10n.chat.mediaDetails.title}
          onLeadingTap={handleBack}
        />

        <div
          style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <ExpauseLoadingIndicator />
        </div>
      </div>
    );
  }

  return (
    <div {...qaAttr({ id: 'media-details', status })} style={containerStyle}>
      <ExpauseAppBar
        rootQa={{ id: 'media-details-app-bar' }}
        title={l10n.chat.mediaDetails.title}
        onLeadingTap={handleBack}
      />

      {/* Body */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: responsive.spacing.base,
        }}
      >
        {status === 'error' && (
          <span
            style={{
              color: theme.danger,
              fontSize: responsive.font.base,
              textAlign: 'center',
            }}
          >
            {errorMessage}
          </span>
        )}

        {status === 'ready' && media?.kind === MediaType.IMAGE && (
          // The full-image bytes are E2EE ciphertext — `src` is the bucket-
          // RELATIVE object path; `EncryptedCachedImage` resolves the tokenized
          // download URL via the domain UseCase (Finding 5) and owns the fetch →
          // decrypt → `blob:` hop + the shared decrypted-blob LRU (same component
          // the inline `MediaBubbleFrame` thumbnail uses).
          // The page is only reachable from the open chat, where these bytes
          // were already decrypted+cached as a thumbnail and the peer key is
          // already derived, so the shared cache is the intended path. Any
          // fetch/decrypt/decode failure is reported via `onImageError`, which
          // flips the page status to `error`. Its `data-qa-id` is suffixed so it
          // never collides with the page root's canonical `media-details` id;
          // the component's own status reflects the decrypt lifecycle.
          <EncryptedCachedImage
            src={media.fullImagePath}
            peerPublicJwk={routeArgs?.peerPublicJwk ?? null}
            qaId="media-details-image"
            onError={onImageError}
            style={{
              maxWidth: '100%',
              maxHeight: responsive.mediaPlayer.maxHeight,
              objectFit: 'contain',
              borderRadius: responsive.borderRadius.base,
            }}
          />
        )}

        {status === 'ready' && media?.kind === MediaType.VIDEO && (
          videoPlaybackError ? (
            <span
              style={{
                color: theme.danger,
                fontSize: responsive.font.base,
                textAlign: 'center',
              }}
            >
              {l10n.chat.mediaDetails.videoError}
            </span>
          ) : (
            // Shared, themed inline player (progress bar + native fullscreen) —
            // same playback surface used across the app. The thumbnail is the
            // message's E2EE thumbnail, rendered through `EncryptedCachedImage`
            // with the route-arg peer key (the player can't take a plain URL).
            <InlineVideoPlayer
              screenKey={screenKey}
              videoId={documentId ?? ''}
              videoUrl={media.videoUrl}
              decryptionKeys={media.decryptionKeys}
              aspectRatio={routeArgs?.message.aspectRatio}
              thumbnail={
                routeArgs?.message.relativeThumbnailPath ? (
                  <EncryptedCachedImage
                    src={routeArgs.message.relativeThumbnailPath}
                    peerPublicJwk={routeArgs.peerPublicJwk ?? null}
                    qaId="media-details-video-thumb"
                    style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                  />
                ) : undefined
              }
              onError={() => setVideoPlaybackError(true)}
            />
          )
        )}
      </div>
    </div>
  );
};
